import Swal from "sweetalert2";
import { formatNumberSync } from "@/utils/numberFormatter";
import { fetchOperationRates, operationRateFor } from "./operationRates";

/**
 * Cambio de moneda de un gasto ya guardado (ticket #397).
 *
 * El sistema no puede saber si el importe estaba bien y se quiere expresar en otra moneda
 * (120 USD → 90,65 GBP) o si la moneda estaba mal cargada y la factura ya era de 120 GBP.
 * Por eso pregunta: convertir con el tipo de cambio de la operación, o mantener el número.
 * Con pagos aplicados no se ofrece convertir: lo pagado quedaría por encima del importe.
 */

function idOf(value) {
  if (value === null || value === undefined || value === "") return "";
  return typeof value === "object" && value._id != null
    ? String(value._id)
    : String(value);
}

/**
 * Ruta de la fila si `path` es la moneda de un gasto del embarque, si no null.
 * Ej.: "group_expenses.expenses.3.currencyex" → "group_expenses.expenses.3".
 */
export function expenseRowPathForCurrencyChange(selectedModel, path) {
  if (String(selectedModel || "").toLowerCase() !== "shipping") return null;
  if (typeof path !== "string") return null;
  const parts = path.split(".");
  if (parts.length < 3 || parts[parts.length - 1] !== "currencyex") return null;
  if (
    !/^\d+$/.test(parts[parts.length - 2]) ||
    parts[parts.length - 3] !== "expenses"
  )
    return null;
  return parts.slice(0, -1).join(".");
}

function money(value) {
  return formatNumberSync(value, null, true, 2);
}

/**
 * Pregunta qué hacer con el importe y, si se elige convertir, llama a `onConvert` con el
 * importe nuevo. No hace nada si el módulo está apagado, el gasto es nuevo, no tiene
 * importe o la moneda no cambió.
 *
 * @param {Object} options
 * @param {string} options.shippingId
 * @param {Object} options.row fila del gasto (antes de convertir)
 * @param {*} options.previousCurrency moneda que tenía
 * @param {*} options.nextCurrency moneda elegida
 * @param {(id: string) => string} options.labelFor etiqueta de una moneda
 * @param {Function} options.t traducción ($t)
 * @param {(amount: number) => void} options.onConvert
 */
export async function offerExpenseAmountConversion({
  shippingId,
  row,
  previousCurrency,
  nextCurrency,
  labelFor,
  t,
  onConvert,
}) {
  const fromId = idOf(previousCurrency);
  const toId = idOf(nextCurrency);
  const amount = Number(row && row.amount);
  if (!shippingId || !row || !row._id || !fromId || !toId || fromId === toId)
    return;
  if (!Number.isFinite(amount) || amount <= 0) return;

  const fx = await fetchOperationRates(shippingId, [fromId, toId]);
  if (!fx.enabled) return;

  const from = labelFor(fromId) || "";
  const to = labelFor(toId) || "";
  const applied = Number(row.paid) || 0;
  const hasPayments =
    applied > 0.009 || (Array.isArray(row.payments) && row.payments.length > 0);
  if (hasPayments) {
    await Swal.fire({
      icon: "info",
      title: t("exchangeRates.expenseChange.title"),
      html: t("exchangeRates.expenseChange.hasPayments", {
        amount: money(amount),
        applied: money(applied),
        from,
        currency: to,
      }),
    });
    return;
  }

  const rateFrom = operationRateFor(fx, fromId);
  const rateTo = operationRateFor(fx, toId);
  if (!rateFrom || !rateTo) {
    await Swal.fire({
      icon: "warning",
      title: t("exchangeRates.expenseChange.title"),
      text: t("exchangeRates.expenseChange.missingRate", {
        amount: money(amount),
        currency: to,
      }),
    });
    return;
  }

  const converted = Math.round(((amount * rateFrom) / rateTo) * 100) / 100;
  const result = await Swal.fire({
    icon: "question",
    title: t("exchangeRates.expenseChange.title"),
    html: t("exchangeRates.expenseChange.question", {
      amount: money(amount),
      from,
      to,
    }),
    showDenyButton: true,
    confirmButtonText: t("exchangeRates.expenseChange.convert", {
      amount: money(converted),
      currency: to,
    }),
    denyButtonText: t("exchangeRates.expenseChange.keep", {
      amount: money(amount),
      currency: to,
    }),
    footer: t("exchangeRates.expenseChange.rateHint", {
      from,
      to,
      rate: (rateFrom / rateTo).toLocaleString(undefined, {
        maximumFractionDigits: 6,
      }),
    }),
  });
  if (result.isConfirmed) onConvert(converted);
}
